// Jump-to-required — the DOM half of <Modal>'s Save check.
//
// Thomas's 3 Oct ask, ruled Modal-wide by Jason on 5 Oct: tap Save with
// something missing and the form JUMPS to that field, rings it red and says
// "Required" under it — instead of the old `submitDisabled` grey-out, where
// Save just went dead and never said why, and on a phone the empty field was
// usually scrolled out of sight.
//
// The Modal runs `findMissingRequired(form)` before it calls `onSubmit`. This
// file is the rule for what counts as "missing", kept pure so it is tested
// without a Modal around it.
//
// WHAT COUNTS
//
//   • A native <input>/<select>/<textarea> with `required`, judged by
//     `validity.valueMissing` — which the browser computes even though the
//     Modal's form is `noValidate`. Plus whitespace-only text: every page this
//     replaces guarded with `!name.trim()`, and "   " is not a name.
//   • A CUSTOM control (not a native field) that opts in — see
//     `requiredWidgetProps` below.
//
// WHAT DOES NOT — deliberately
//
//   • step / min / max / pattern / type mismatches. Modal is `noValidate`
//     because browser constraint validation refused values the page itself
//     considered valid (a 0.25 kg in a step="0.1" box). This is an EMPTINESS
//     check and nothing else; range and format rules stay the page's own.
//   • disabled and read-only controls — the person cannot fill them.
//   • controls that are not rendered (display:none, [hidden], [inert],
//     aria-hidden) — a field the person cannot see is not one we can jump to.
//     The page's own mutation guard is still the belt for those.

import { createContext } from 'react'

/** What a submitting <Modal> tells the <Field>s inside it: which controls it
 *  flagged at the last Save, and which surface it is drawn on (the "Required"
 *  line needs a different red on white than on the dark shell). Null outside
 *  a submitting Modal — a Field there never shows the line. */
interface RequiredFieldsState {
  missing: readonly Element[]
  surface: 'dark' | 'light'
}
export const RequiredFieldsContext = createContext<RequiredFieldsState | null>(null)

/** Set on a custom control's focusable element to opt it in. */
const REQUIRED_ATTR = 'data-required'
/** 'true' while the custom control has no value, 'false' once it has one. */
const EMPTY_ATTR = 'data-required-empty'
/** What to call the custom control in the screen-reader announcement. */
const CAPTION_ATTR = 'data-required-caption'
/** Set by the Modal on every control it flagged — the CSS hook for the ring. */
const MISSING_ATTR = 'data-required-missing'

/** The opt-in convention for a CUSTOM control (anything that is not a native
 *  input/select/textarea — SearchableSelect first). Spread the result onto the
 *  element that takes FOCUS (for SearchableSelect, its trigger button), so the
 *  Modal can jump to it:
 *
 *    <button … {...requiredWidgetProps({ required, empty: !value, caption: label })}>
 *
 *  Data attributes, not `aria-required`: the focusable element of most custom
 *  pickers is a <button>, and `aria-required` is not a supported property of
 *  role=button (jsx-a11y's role-supports-aria-props, which check-jsx-a11y
 *  enforces). Not required → no attributes at all, so the control renders
 *  exactly as before. */
export function requiredWidgetProps(p: { required?: boolean; empty: boolean; caption?: string }): Record<string, string> {
  if (!p.required) return {}
  const attrs: Record<string, string> = { [REQUIRED_ATTR]: 'true', [EMPTY_ATTR]: p.empty ? 'true' : 'false' }
  if (p.caption) attrs[CAPTION_ATTR] = p.caption
  return attrs
}

type NativeField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

function isNative(el: Element): el is NativeField {
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
}

/** Input types where a run of spaces is as empty as nothing. Not password —
 *  a secret is the person's own business. */
const TEXT_TYPES = new Set(['text', 'search', 'email', 'tel', 'url'])

/** Can the person fill it at all? Not when it is disabled or read-only — and
 *  a <select> has no read-only state, so the attribute does not excuse one. */
function isFillable(el: NativeField): boolean {
  if (el.disabled) return false
  return el instanceof HTMLSelectElement || !el.readOnly
}

/** Spaces alone, in a box where spaces are as empty as nothing. */
function isBlankText(el: NativeField): boolean {
  const texty = el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && TEXT_TYPES.has(el.type))
  return texty && el.value.trim() === ''
}

function nativeIsEmpty(el: NativeField): boolean {
  if (!el.required || !isFillable(el)) return false
  return el.validity?.valueMissing === true || isBlankText(el)
}

function widgetIsEmpty(el: Element): boolean {
  if (el.getAttribute(REQUIRED_ATTR) !== 'true') return false
  if ((el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return false
  return el.getAttribute(EMPTY_ATTR) === 'true'
}

/** Required and empty — by the rules in the header. Says nothing about
 *  whether it is on screen; `isRendered` is the other half. */
export function isEmptyRequired(el: Element): boolean {
  return isNative(el) ? nativeIsEmpty(el) : widgetIsEmpty(el)
}

/** Is the control actually drawn? `checkVisibility` where the engine has it
 *  (Chrome 105+); the floor's Chrome 86 handsets and jsdom walk the ancestors'
 *  computed display instead. */
function isRendered(el: Element): boolean {
  if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false
  const check = (el as Element & { checkVisibility?: (o?: object) => boolean }).checkVisibility
  if (typeof check === 'function') return check.call(el, { checkVisibilityCSS: true, visibilityProperty: true })
  const view = el.ownerDocument.defaultView
  if (!view) return true
  for (let n: Element | null = el; n; n = n.parentElement) {
    const cs = view.getComputedStyle(n)
    if (cs.display === 'none') return false
    if (n === el && cs.visibility === 'hidden') return false
  }
  return true
}

/** Still something the Modal should be flagging. */
export function stillMissing(el: Element): boolean {
  return el.isConnected && isEmptyRequired(el) && isRendered(el)
}

const CANDIDATES = `input[required], select[required], textarea[required], [${REQUIRED_ATTR}="true"]`

/** Every required control in `form` that is empty and on screen, in DOCUMENT
 *  order (the first is the one the Modal jumps to). A control belonging to a
 *  different form — a nested one — is that form's business. */
export function findMissingRequired(form: HTMLFormElement): Element[] {
  return Array.from(form.querySelectorAll(CANDIDATES)).filter(el => {
    const owner = isNative(el) ? el.form : el.closest('form')
    return owner === form && stillMissing(el)
  })
}

// Flagging stores what aria-invalid was BEFORE, so clearing restores a page's
// own value instead of deleting it. Nothing in src sets aria-invalid today;
// this keeps it that way if a page ever does.
const priorInvalid = new WeakMap<Element, string | null>()

/** Ring + aria-invalid on a flagged control. */
export function markMissing(el: Element): void {
  if (el.hasAttribute(MISSING_ATTR)) return
  priorInvalid.set(el, el.getAttribute('aria-invalid'))
  el.setAttribute(MISSING_ATTR, '')
  el.setAttribute('aria-invalid', 'true')
}

/** Undo `markMissing`. */
export function unmarkMissing(el: Element): void {
  if (!el.hasAttribute(MISSING_ATTR)) return
  el.removeAttribute(MISSING_ATTR)
  const prior = priorInvalid.get(el)
  if (prior == null) el.removeAttribute('aria-invalid')
  else el.setAttribute('aria-invalid', prior)
  priorInvalid.delete(el)
}

/** A non-Field <label>'s own words — not its control's value or options, and
 *  not anything hidden from assistive tech. */
function labelText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? ''
  if (!(node instanceof Element)) return ''
  if (/^(INPUT|SELECT|TEXTAREA|BUTTON|OPTION)$/.test(node.tagName)) return ''
  if (node.getAttribute('aria-hidden') === 'true') return ''
  return Array.from(node.childNodes).map(labelText).join(' ')
}

function clean(s: string | null | undefined): string {
  // Field puts its house asterisk inside the caption; it is not part of the name.
  return (s ?? '').replace(/\*/g, '').replace(/\s+/g, ' ').trim()
}

/** What to call the control when announcing it: the <Field>'s own caption,
 *  then a custom control's declared caption, then whatever the browser would
 *  have named it by. */
export function captionOf(el: Element): string {
  const field = el.closest('label[data-field-caption]')
  const candidates = [
    field?.getAttribute('data-field-caption'),
    el.getAttribute(CAPTION_ATTR),
    el.getAttribute('aria-label'),
    isNative(el) && el.labels?.[0] ? labelText(el.labels[0]) : null,
    el.getAttribute('aria-labelledby')
      ?.split(/\s+/).map(id => el.ownerDocument.getElementById(id)?.textContent ?? '').join(' '),
    el.getAttribute('placeholder'),
  ]
  for (const c of candidates) {
    const s = clean(c)
    if (s) return s
  }
  return 'A required field'
}

/** The one sentence the Modal's live region speaks after a refused Save. */
export function missingAnnouncement(missing: Element[]): string {
  if (missing.length === 0) return ''
  const first = `${captionOf(missing[0])} is required.`
  const rest = missing.length - 1
  if (rest === 0) return first
  return `${first} ${rest} more required ${rest === 1 ? 'field is' : 'fields are'} empty.`
}
